import { useState } from "react";
import { Check, Clipboard, FileCode, FileDown, Mail, Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export function ReportToolbar({ plainText, markdown, emailSummary }) {
  const [copiedText, setCopiedText] = useState(false);
  const [copiedMarkdown, setCopiedMarkdown] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);

  const copy = async (kind) => {
    let payload;
    if (kind === "markdown") payload = markdown;
    else if (kind === "email") payload = emailSummary;
    else payload = plainText;
    try {
      await navigator.clipboard.writeText(payload);
      if (kind === "markdown") {
        setCopiedMarkdown(true);
        toast.success("Report copied as markdown", {
          description: "Paste into your governance document or email."
        });
        setTimeout(() => setCopiedMarkdown(false), 2000);
      } else if (kind === "email") {
        setCopiedEmail(true);
        toast.success("Email summary copied", {
          description: "Short summary is on your clipboard \u2014 paste into an email draft."
        });
        setTimeout(() => setCopiedEmail(false), 2000);
      } else {
        setCopiedText(true);
        toast.success("Report copied as plain text", {
          description: "Paste into any consultation document or notes."
        });
        setTimeout(() => setCopiedText(false), 2000);
      }
    } catch {
      toast.error("Unable to copy automatically. Please copy the visible report manually.");
    }
  };

  const handlePrint = () => {
    try {
      window.print();
    } catch {
      toast.error("Print dialog could not be opened.");
    }
  };

  // Browser-native Save-as-PDF: same rendered report, no data or meaning changes.
  const handleDownloadPdf = () => {
    try {
      toast.info("Save as PDF", {
        description: "In the print dialog, choose \u201cSave as PDF\u201d as the destination."
      });
      setTimeout(() => window.print(), 350);
    } catch {
      toast.error("Print dialog could not be opened.");
    }
  };

  return (
    <div
      data-testid="report-toolbar"
      className="report-no-print sticky top-16 z-20 mt-6 flex flex-wrap items-center justify-between gap-3 rounded-sm border border-border bg-card/95 px-4 py-3 shadow-paper backdrop-blur-md"
    >
      <p className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
        Copy / Print Tools
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => copy("text")}
          className="rounded-sm border-border bg-card text-[13px] font-medium hover:bg-secondary"
          data-testid="copy-report-text"
        >
          {copiedText ? <Check className="h-4 w-4" /> : <Clipboard className="h-4 w-4" />}
          {copiedText ? "Copied" : "Copy as text"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => copy("markdown")}
          className="rounded-sm border-border bg-card text-[13px] font-medium hover:bg-secondary"
          data-testid="copy-report-markdown"
        >
          {copiedMarkdown ? <Check className="h-4 w-4" /> : <FileCode className="h-4 w-4" />}
          {copiedMarkdown ? "Copied" : "Copy as markdown"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => copy("email")}
          className="rounded-sm border-border bg-card text-[13px] font-medium hover:bg-secondary"
          data-testid="copy-report-email"
          disabled={!emailSummary}
        >
          {copiedEmail ? <Check className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
          {copiedEmail ? "Copied" : "Copy as email summary"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleDownloadPdf}
          className="rounded-sm border-border bg-card text-[13px] font-medium hover:bg-secondary"
          data-testid="download-report-pdf"
        >
          <FileDown className="h-4 w-4" />
          Download as PDF
        </Button>
        <Button
          type="button"
          onClick={handlePrint}
          className="rounded-sm px-5 text-[13px] font-medium tracking-wide shadow-paper"
          data-testid="print-report"
        >
          <Printer className="h-4 w-4" />
          Print report
        </Button>
      </div>
    </div>
  );
}
